import { addDays, format, formatDuration, intervalToDuration, isAfter, isSameDay, isValid, parseISO } from 'date-fns';
import enUS from 'date-fns/locale/en-US';

// noinspection SpellCheckingInspection
export enum DATETIME_FORMATS {
  /** Example: `2017-03-01` */
  yyyyMMdd = 'yyyy-MM-dd',
  /** Example: `2017-03-01 (Wed)` */
  yyyyMMddiii = 'yyyy-MM-dd (iii)',
  /** Example: `Mar 01, 2017` */
  MMMddyyyy = 'MMM dd, yyyy',
  /** Example: `Mar 01, 2017 (Wed)` */
  MMMddyyyyiii = 'MMM dd, yyyy (iii)',
  /** Example: `2017-03` */
  yyyyMM = 'yyyy-MM',
  /** Example: `2017 - Mar` */
  yyyyMMM = 'yyyy - MMM',
  /** Example: `Mar 01, 2017 13:00:25` */
  MMMMddyyyyHHmmss = 'MMM dd, yyyy HH:mm:ss',
  /** Example: `Mar 01, 2017 13:00:25 (+07:00)` */
  MMMMddyyyyHHmmssXXX = 'MMM dd, yyyy HH:mm:ss (XXX)',
  /** Example: `13:00:25` */
  HHmmss = 'HH:mm:ss',
}

export class DateUtils {
  /** The current month as `yyyy-MM`. */
  static getCurrentYearMonth = () => format(new Date(), DATETIME_FORMATS.yyyyMM);

  static dateAnyMindStarts = new Date(2016, 3, 1); // 2016-04-01
  static datePerformanceReviewStarts = new Date(2025, 0, 1); // 2025 Q1/Q2

  static parseDate = (date: Date | number | string | null | undefined): Date | null => {
    if (date === null || date === undefined) {
      return null;
    }

    try {
      const parsedDate = typeof date === 'string' ? parseISO(date) : date;
      return isValid(parsedDate) ? new Date(parsedDate) : null;
    } catch (_error) {
      return null;
    }
  };

  static formatDate = (
    date: number | Date | string | null | undefined,
    formatting: string | DATETIME_FORMATS = DATETIME_FORMATS.yyyyMMdd,
  ): string => {
    try {
      date = typeof date === 'string' ? parseISO(date) : date;
      return date && isValid(date)
        ? format(date, formatting || DATETIME_FORMATS.yyyyMMdd, {
            locale: enUS,
          })
        : '';
    } catch (_error) {
      return '';
    }
  };

  static formatDateTime = (value: number | Date | string | null | undefined): string =>
    DateUtils.formatDate(value, DATETIME_FORMATS.MMMMddyyyyHHmmss);

  static getMinuteDifference = (startDate: Date, endDate: Date): number => {
    try {
      return (endDate.getTime() - startDate.getTime()) / 60000;
    } catch (_error) {
      return 0;
    }
  };

  static getDifferenceString = (
    date: Date | number | string | null | undefined,
  ): { text: string; isFuture: boolean } => {
    const today = new Date();
    let parsedDate = DateUtils.parseDate(date);
    if (!parsedDate) {
      return { text: '', isFuture: true };
    }
    if (isSameDay(parsedDate, today)) {
      return { text: 'Today', isFuture: true };
    }
    const isFuture = isAfter(parsedDate, today);
    parsedDate = addDays(parsedDate, isFuture ? 1 : 0);

    const text = [
      isFuture ? 'In ' : '',
      formatDuration(intervalToDuration({ start: parsedDate, end: today }), {
        format: ['years', 'months', 'days'],
        locale: enUS,
      }),
    ].join('');

    return { text, isFuture };
  };

  static coalesce = (...dates: any[]): Date | undefined => {
    for (const date of dates) {
      const parsedDate = DateUtils.parseDate(date);
      if (parsedDate && isValid(parsedDate)) {
        return parsedDate;
      }
    }

    return undefined;
  };

  static getCurrentTimezone = (): string => {
    try {
      const resolvedOptions = Intl.DateTimeFormat().resolvedOptions();
      const timeZone = resolvedOptions?.timeZone;

      if (timeZone && timeZone.trim() !== '') {
        return timeZone;
      }

      return 'UTC';
    } catch (_error) {
      return 'UTC';
    }
  };
}
